import type { Feature, FeatureCollection, Point } from 'geojson';

import { sourceIndexOf } from './traces-geometry-layer';

export interface LocatedFinding {
  featureIndex: number;
  at?: [number, number];
  kind?: 'repaired' | 'collapsed';
}

export interface TracesFindingItem {
  code: string;
  message?: string;
  place?: number;
  sourceFeature?: number | null;
  point?: [number, number];
}

export type PrecisionCode = 'PRECISION_REPAIRED' | 'PRECISION_COLLAPSED' | 'HOLES_UNSUPPORTED' | 'REPAIR_SPLIT';

const PRECISION_KIND: Record<PrecisionCode, 'repaired' | 'collapsed'> = {
  PRECISION_REPAIRED: 'repaired',
  PRECISION_COLLAPSED: 'collapsed',
  HOLES_UNSUPPORTED: 'collapsed',
  REPAIR_SPLIT: 'collapsed',
};

export function isPrecisionCode(code: string): code is PrecisionCode {
  return Object.hasOwn(PRECISION_KIND, code);
}

export interface GeoCheckFinding {
  code: string;
  featureIndex?: number;
  sourceFeature?: number | null;
  at?: [number, number];
}

export const TRACES_AMBER = '#ff9100';
export const PRECISION_VIOLET = '#7c4dff';

export function tracesFindingsToLocated(items: TracesFindingItem[]): LocatedFinding[] {
  return (items ?? []).map((item) => ({
    featureIndex: item.sourceFeature ?? (item.place != null ? item.place - 1 : -1),
    at: item.point,
  }));
}

function originalIndex(
  sourceFeature: number | null | undefined,
  derivedIndex: number,
  traces: FeatureCollection | undefined
): number {
  if (sourceFeature != null) return sourceFeature;
  return derivedIndex < 0 ? derivedIndex : sourceIndexOf(traces, derivedIndex);
}

export function precisionFindingsToLocated(findings: GeoCheckFinding[], traces?: FeatureCollection): LocatedFinding[] {
  return (findings ?? [])
    .filter(
      (f): f is GeoCheckFinding & { featureIndex: number; code: PrecisionCode } =>
        isPrecisionCode(f.code) && f.featureIndex != null
    )
    .map((f) => ({
      featureIndex: originalIndex(f.sourceFeature, f.featureIndex, traces),
      at: f.at,
      kind: PRECISION_KIND[f.code],
    }));
}

export function outlineFeatures(geo: FeatureCollection | undefined, located: LocatedFinding[]): Feature[] {
  const features = geo?.features ?? [];
  const out: Feature[] = [];
  for (const l of located) {
    const source = features[l.featureIndex];
    if (source?.geometry)
      out.push({ type: 'Feature', properties: { kind: l.kind ?? null }, geometry: source.geometry });
  }
  return out;
}

export function pointFeatures(located: LocatedFinding[]): Feature<Point>[] {
  return located
    .filter((l): l is LocatedFinding & { at: [number, number] } => l.at != null)
    .map((l) => ({
      type: 'Feature',
      properties: { kind: l.kind ?? null },
      geometry: { type: 'Point', coordinates: l.at },
    }));
}

export function tracesLayerPaint(stale: boolean): { circle: object; line: object } {
  const opacity = stale ? 0.4 : 1;
  return {
    circle: {
      'circle-radius': 7,
      'circle-color': TRACES_AMBER,
      'circle-stroke-width': 2,
      'circle-stroke-color': '#fff',
      'circle-opacity': opacity,
      'circle-stroke-opacity': opacity,
    },
    line: { 'line-color': TRACES_AMBER, 'line-width': 3, 'line-opacity': opacity },
  };
}

export function precisionLayerPaint(): { circle: object; line: object } {
  return {
    circle: {
      'circle-radius': ['match', ['get', 'kind'], 'collapsed', 9, 6],
      'circle-color': PRECISION_VIOLET,
      'circle-opacity': ['match', ['get', 'kind'], 'collapsed', 1, 0.55],
      'circle-stroke-width': 2,
      'circle-stroke-color': '#fff',
    },
    line: {
      'line-color': PRECISION_VIOLET,
      'line-width': ['match', ['get', 'kind'], 'collapsed', 3, 1.5],
      'line-opacity': ['match', ['get', 'kind'], 'collapsed', 1, 0.55],
    },
  };
}
